"use client"

import {AppFooter} from "@/app/components/common/app-footer/AppFooter";
import HeroCarousel from "@/app/components/hero/HeroCarousel";
import MobileSearchIconBtn from "@/app/components/game/MobileSearchIconBtn";
import {Tabs, TabsList, TabsTrigger} from "@/app/components/common/tab-selector/TabSelector";
import {FILTER_TABS} from "@/lib/utils/contants/heroFilterTabs";
import React, {Suspense, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDebounce} from "use-debounce";
import {useGetLobbyQuery} from "@/lib/api/games/gameApi";
import {FilterTabsEnum} from "@/lib/enums/FilterTabsEnum";
import CenterBtn from "@/app/components/common/center-btn/CenterBtn";
import SearchInput from "@/app/components/common/search-input/SearchInput";
import {useTranslations} from "next-intl";
import {useRouter, useSearchParams} from "next/navigation";
import {useAppDispatch, useAppSelector, useAppStore} from "@/lib/hooks";
import {AuthTypesEnum} from "@/lib/enums/AuthTypesEnum";
import {setIsFundsCredited} from "@/lib/slices/uiSlice";
import {showAppToast} from "@/app/components/common/app-toast/showAppToast";
import {usePathname} from "@/i18n/navigation";
import {WalletTabsEnum} from "@/lib/enums/WalletTabsEnum";
import {LobbyTab} from "@/app/components/hero/LobbyTab";
import {LobbyExpandedTab} from "@/app/components/hero/LobbyExpandedTab";
import styles from './styles.module.css';
import {CategoryTab} from "@/app/components/hero/CategoryTab";
import {useGetHasDepositsQuery} from "@/lib/api/wallet/profileApi";
import {useGetBonusesQuery} from "@/lib/api/wallet/bonusApi";
import {WelcomeBonusStatusesEnum} from "@/lib/enums/WelcomeBonusStatusesEnum";
import {
    WELCOME_BONUS_JUST_REGISTERED_KEY,
    AUTH_SESSION_ID_KEY,
    WELCOME_BONUS_SHOWN_SESSION_ID_KEY,
    UNLOCK_NEXT_BONUS_SHOWN_SESSION_ID_KEY
} from "@/lib/utils/contants/welcomeBonusStorage";
import {useRequestDialog} from "@/lib/dialog-orchestrator/react";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {safeSessionStorage} from "@/lib/utils/storage";
import {useLobbyCategories} from "@/hooks/use-lobby-categories";
import {LOBBY_BATCH_MAX_CATEGORIES} from "@/lib/utils/contants/lobbyCategories";
import {LobbyCategoryTypeEnum} from "@/lib/enums/LobbyCategoryTypeEnum";

export default function Home() {
    const isFundsCredited = useAppSelector(state => state.ui.isFundsCredited);
    const [currentTab, setCurrentTab] = useState(FILTER_TABS[0].value);
    const [search, setSearch] = useState('');
    const [debouncedSearchTerm] = useDebounce(search, 500);
    const size = 20;
    const t = useTranslations();
    const searchParams = useSearchParams();
    const hasAuthQuery = searchParams.has("uid") && searchParams.has("token");
    const hasOpenRegisterQuery = searchParams.has("open");
    const hasDepositSuccess = searchParams.has("deposit") && searchParams.get("deposit") === "success";
    const hasDepositPending = searchParams.has("deposit") && searchParams.get("deposit") === "pending";
    const router = useRouter();
    const requestDialog = useRequestDialog();
    const isAuthorized = useAppSelector(state => !!state.auth.userId);
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const pathname = usePathname();

    // Thin imperative openers — the orchestrator owns ordering, layering,
    // dependencies (e.g. WALLET requiresAuth) and rendering via DialogHost.
    // Every opener on this page is reached from a bonus/auth dialog, never from a
    // bare page control — hence `popup`.
    const openWallet = useCallback(
        () => requestDialog(DialogsEnum.WALLET, {props: {tab: WalletTabsEnum.DEPOSIT, openSource: "popup"}}),
        [requestDialog],
    );
    const openAuthRegister = useCallback(
        () => requestDialog(DialogsEnum.AUTH, {props: {tab: AuthTypesEnum.REGISTER}}),
        [requestDialog],
    );
    const openWelcomeBonus = useCallback(
        () => requestDialog(DialogsEnum.WELCOME_BONUS),
        [requestDialog],
    );

    useEffect(() => {
        if (hasDepositSuccess) {
            dispatch(setIsFundsCredited(true));
        }
    }, [dispatch, hasDepositSuccess])

    useEffect(() => {
        if (hasDepositSuccess && !isFundsCredited) {
            window.history.replaceState(null, '', pathname)
        }
    }, [hasDepositSuccess, isFundsCredited, pathname]);

    // AC-851: the provider returned the player before the payment was confirmed (or after they
    // abandoned it). Say so — the balance updates from the provider callback / reconciliation,
    // never from this URL.
    const pendingNoticeShownRef = useRef(false);
    useEffect(() => {
        if (!hasDepositPending || pendingNoticeShownRef.current) return;
        pendingNoticeShownRef.current = true;
        showAppToast({title: t("Processing.title"), description: t("Processing.description"), type: "info"});
        window.history.replaceState(null, '', pathname);
    }, [hasDepositPending, pathname, t]);

    // Reset-password link (?uid&token): open SET_NEW_PASSWORD, then clean the URL.
    useEffect(() => {
        if (!hasAuthQuery) return;
        let cancelled = false;
        requestDialog(DialogsEnum.SET_NEW_PASSWORD).then(() => {
            if (!cancelled) router.replace(`/`, {scroll: false});
        });
        return () => {
            cancelled = true;
        };
    }, [hasAuthQuery, requestDialog, router]);

    // Register link (?open): open AUTH; on success open the cashier, then clean URL.
    // AC-935: partner links carry ?open=register for everyone, so a signed-in player
    // skips the registration and goes straight to the cashier it leads to. Read from
    // the store, not a dep: the auth flip after a successful registration must not
    // re-run this effect and cancel the pending result.
    useEffect(() => {
        if (!hasOpenRegisterQuery) return;
        if (store.getState().auth.userId) {
            openWallet();
            router.replace(`/`, {scroll: false});
            return;
        }
        let cancelled = false;
        requestDialog(DialogsEnum.AUTH, {props: {tab: AuthTypesEnum.REGISTER}}).then((result) => {
            if (cancelled) return;
            if (result.success) openWallet();
            router.replace(`/`, {scroll: false});
        });
        return () => {
            cancelled = true;
        };
    }, [hasOpenRegisterQuery, requestDialog, router, openWallet, store]);

    // AC-819: the lobby structure comes from the backend, resolved per GEO.
    const {categories: lobbyCategories, country, isLoading: categoriesLoading} = useLobbyCategories();
    // The batch endpoint caps the number of category slugs per request.
    const gameCategorySlugs = useMemo(() => lobbyCategories
        .filter(category => category.type !== LobbyCategoryTypeEnum.PROVIDERS)
        .map(category => category.slug)
        .slice(0, LOBBY_BATCH_MAX_CATEGORIES), [lobbyCategories]);
    const includeProducers = useMemo(() => lobbyCategories
        .some(category => category.type === LobbyCategoryTypeEnum.PROVIDERS), [lobbyCategories]);

    // One batch request for every lobby row + the producers strip (was 6
    // parallel requests, one per row).
    const {data: lobby, isLoading: lobbyLoading} = useGetLobbyQuery({
        categories: gameCategorySlugs,
        size,
        country,
        includeProducers,
    }, {skip: !gameCategorySlugs.length})

    const {data: bonuses, isLoading: bonusesLoading} = useGetBonusesQuery(undefined, {skip: !isAuthorized});
    const {data: hasDeposits, isLoading: hasDepositsLoading} = useGetHasDepositsQuery(undefined, {skip: !isAuthorized});

    const isBurnedStatus = (status: WelcomeBonusStatusesEnum | null | undefined) =>
        status === WelcomeBonusStatusesEnum.BURN;

    const isWelcomeBonusAvailable = useMemo(() => {
        return !bonuses?.find(bonus => isBurnedStatus(bonus.status)) || false;
    }, [bonuses]);

    const nextClaimBonus = useMemo(() => {
        const hasBurned = bonuses?.some(b => isBurnedStatus(b.status));
        if (!hasBurned) return null;
        return bonuses?.find(b =>
            b.status === WelcomeBonusStatusesEnum.CLAIM &&
            new Date(b.activatableUntil) > new Date()
        ) ?? null;
    }, [bonuses]);

    useEffect(() => {
        if (!isAuthorized || bonusesLoading || hasDepositsLoading) return;
        if (hasDeposits?.hasDeposits) return;
        if (!isWelcomeBonusAvailable) return;
        if (typeof window === 'undefined') return;

        const justRegistered = safeSessionStorage.getItem(WELCOME_BONUS_JUST_REGISTERED_KEY) === 'true';
        if (!justRegistered) return;

        const sessionId = safeSessionStorage.getItem(AUTH_SESSION_ID_KEY);
        if (!sessionId) return;

        const shownSessionId = safeSessionStorage.getItem(WELCOME_BONUS_SHOWN_SESSION_ID_KEY);
        if (shownSessionId === sessionId) return;

        const timeoutId = window.setTimeout(() => {
            safeSessionStorage.setItem(WELCOME_BONUS_SHOWN_SESSION_ID_KEY, sessionId);
            // The orchestrator owns ordering/mutual-exclusion against other dialogs.
            requestDialog(DialogsEnum.WELCOME_BONUS_PROMO);
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [bonusesLoading, hasDeposits?.hasDeposits, hasDepositsLoading, isAuthorized, isWelcomeBonusAvailable, requestDialog]);

    useEffect(() => {
        if (!isAuthorized || bonusesLoading || !nextClaimBonus) return;
        if (isFundsCredited) return;
        if (typeof window === 'undefined') return;

        const sessionId = safeSessionStorage.getItem(AUTH_SESSION_ID_KEY);
        if (!sessionId) return;

        const shownSessionId = safeSessionStorage.getItem(UNLOCK_NEXT_BONUS_SHOWN_SESSION_ID_KEY);
        if (shownSessionId === sessionId) return;

        const timeoutId = window.setTimeout(() => {
            safeSessionStorage.setItem(UNLOCK_NEXT_BONUS_SHOWN_SESSION_ID_KEY, sessionId);
            // "Continue" resolves the dialog successfully → open the cashier.
            requestDialog(DialogsEnum.UNLOCK_NEXT_BONUS, {
                props: {minDeposit: nextClaimBonus.minDeposit},
            }).then(({success}) => {
                if (success) openWallet();
            });
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [bonusesLoading, isFundsCredited, isAuthorized, nextClaimBonus, requestDialog, openWallet]);

    const onSlideClick = (index: number) => {
        if (!isAuthorized) {
            openAuthRegister();
            return;
        }
        if (index === 2) {
            openWelcomeBonus();
        } else {
            openWallet();
        }
    }

    return (
        <div className={'bg-dark-gray pt-6'}>
            <div className={'mb-6 ml-[16px] md:ml-[32px] md:mr-[32px]'}>
                <HeroCarousel onSlideClick={onSlideClick}/>
            </div>
            <div className={'flex mb-6 gap-2 md:gap-[5px] lg:gap-[15px] px-4 md:px-8'}>
                <MobileSearchIconBtn className={'md:hidden'}/>
                <div className={'flex-1 md:flex-3/5 grow rounded-[12px] overflow-x-scroll'}
                     style={{scrollbarWidth: 'none'}}>
                    <Tabs className={'w-full flex-row justify-start items-center'}
                          onValueChange={(value) => setCurrentTab(value as FilterTabsEnum)}
                          defaultValue={FILTER_TABS[0].value}>
                        <TabsList className={'flex-1 grow h-12'}>
                            {FILTER_TABS.map((i) => (
                                <TabsTrigger className={'mr-[4px] last:mr-0 rounded-[8px]'} value={i.value}
                                             key={i.value} asChild>
                                    <CenterBtn aria-controls={`${i.value}-btn`} id={`${i.value}-btn`}
                                               onClick={() => setCurrentTab(i.value)}
                                               className={'px-[10px] md:px-[12px] lg:px-[8px] flex justify-center'}
                                               active={i.value === currentTab} icon={i.icon} label={t(i.label)}/>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>
                <div className={'hidden md:block flex-1/5 grow'}>
                    <SearchInput value={search} onChange={(value) => setSearch(value.target.value)}/>
                </div>
            </div>

            <div className={'px-4 md:px-8 min-h-[500px]'}>
                <div className={styles.tabContainer}>
                    {currentTab === FilterTabsEnum.LOBBY ?
                        (
                            <div
                                key={search ? "lobby-expanded" : "lobby"}
                                className={styles.fadeIn}
                            >
                                {
                                    !search ?
                                        <LobbyTab categories={lobbyCategories}
                                                  games={lobby?.games || {}}
                                                  producers={lobby?.producers || []}
                                                  fetching={categoriesLoading || lobbyLoading}/> :
                                        <LobbyExpandedTab search={debouncedSearchTerm}/>
                                }
                            </div>
                        ) : (
                            <div
                                key={currentTab}
                                className={styles.fadeIn}
                            >
                                <CategoryTab search={debouncedSearchTerm}
                                             tab={FILTER_TABS.find(i => i.value === currentTab)!}/>
                            </div>
                        )}
                </div>
            </div>

            <Suspense fallback={null}>
                <AppFooter className={'px-4 md:px-8'}/>
            </Suspense>
        </div>
    );
}
